/**
 * useMediaLibrary - Hook for managing stock media state and operations
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import { auth } from "@/lib/firebase";
import {
  MediaType,
  MediaProvider,
  MediaOrientation,
  MediaColor,
  MediaSortOrder,
  VideoSize,
  VideoType,
  MediaLibraryResult,
  LocalMediaAsset,
  DownloadingMedia,
  MEDIA_TYPE_TO_PROVIDER,
  MEDIA_PROVIDERS,
} from "@/types/media-library-panel.types";

interface UseMediaLibraryProps {
  devContainerId?: string;
  previewUrl?: string;
}

interface UseMediaLibraryReturn {
  // Search state
  query: string;
  setQuery: (value: string) => void;
  mediaType: MediaType;
  setMediaType: (value: MediaType) => void;
  provider: MediaProvider | "all";
  setProvider: (value: MediaProvider | "all") => void;
  orientation: MediaOrientation | "any";
  setOrientation: (value: MediaOrientation | "any") => void;
  color: MediaColor | "any";
  setColor: (value: MediaColor | "any") => void;
  sortOrder: MediaSortOrder;
  setSortOrder: (value: MediaSortOrder) => void;
  videoSize: VideoSize | "any";
  setVideoSize: (value: VideoSize | "any") => void;
  videoType: VideoType;
  setVideoType: (value: VideoType) => void;

  // Search results
  searchResults: MediaLibraryResult[];
  totalResults: number;
  isSearching: boolean;
  hasSearched: boolean;
  isShowingCurated: boolean;
  handleSearch: () => Promise<void>;

  // Infinite scroll (search)
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => Promise<void>;

  // Selection
  selectedResults: Set<string>;
  toggleResultSelection: (id: string) => void;
  clearResultSelection: () => void;
  selectAllResults: () => void;

  // Download
  downloadingItems: DownloadingMedia[];
  downloadingIds: Set<string>;
  handleDownload: (items: MediaLibraryResult[]) => Promise<void>;
  handleDownloadSelected: () => Promise<void>;

  // Local assets
  localAssets: LocalMediaAsset[];
  totalLocalAssets: number;
  isLoadingAssets: boolean;
  loadLocalAssets: () => Promise<void>;

  // Local assets pagination
  assetsHasMore: boolean;
  isLoadingMoreAssets: boolean;
  loadMoreAssets: () => Promise<void>;

  // Local asset selection
  selectedAssets: Set<string>;
  toggleAssetSelection: (id: string) => void;
  clearAssetSelection: () => void;
  selectAllAssets: () => void;

  // Local asset deletion
  isDeleting: boolean;
  handleDeleteSelected: () => Promise<void>;

  // Scroll control
  scrollViewportRef: React.RefObject<HTMLDivElement>;
}

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * Strip token query parameters from URL
 */
function stripTokenFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("tkn");
    urlObj.searchParams.delete("token");
    return urlObj.toString();
  } catch {
    return url.replace(/[?&]tkn=[^&]+/, "").replace(/[?&]token=[^&]+/, "");
  }
}

export function useMediaLibrary({
  devContainerId,
  previewUrl,
}: UseMediaLibraryProps): UseMediaLibraryReturn {
  // Search form state
  const [query, setQuery] = useState("");
  const [mediaType, setMediaType] = useState<MediaType>("image");
  const [provider, setProvider] = useState<MediaProvider | "all">(
    MEDIA_TYPE_TO_PROVIDER.image,
  );
  const [orientation, setOrientation] = useState<MediaOrientation | "any">(
    "any",
  );
  const [color, setColor] = useState<MediaColor | "any">("any");
  const [sortOrder, setSortOrder] = useState<MediaSortOrder>("popular");
  const [videoSize, setVideoSize] = useState<VideoSize | "any">("any");
  const [videoType, setVideoType] = useState<VideoType>("all");

  // Search results
  const [searchResults, setSearchResults] = useState<MediaLibraryResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isShowingCurated, setIsShowingCurated] = useState(false);
  const [prevMediaType, setPrevMediaType] = useState<MediaType>(mediaType);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Selection state
  const [selectedResults, setSelectedResults] = useState<Set<string>>(
    new Set(),
  );
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  // Download state
  const [downloadingItems, setDownloadingItems] = useState<DownloadingMedia[]>(
    [],
  );

  // Local assets
  const [localAssets, setLocalAssets] = useState<LocalMediaAsset[]>([]);
  const [totalLocalAssets, setTotalLocalAssets] = useState(0);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Local assets pagination
  const [assetsPage, setAssetsPage] = useState(1);
  const [assetsHasMore, setAssetsHasMore] = useState(false);
  const [isLoadingMoreAssets, setIsLoadingMoreAssets] = useState(false);

  // Mount tracking
  const isMountedRef = useRef(true);
  const isSearchingRef = useRef(false);
  const isInitialMountRef = useRef(true);

  // Track current search params to prevent loadMore with stale params
  const currentSearchParamsRef = useRef({
    mediaType,
    query,
    provider,
    orientation,
    color,
    sortOrder,
    videoSize,
    videoType,
  });

  // Scroll viewport ref for resetting scroll position
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const ASSETS_PER_PAGE = 30;

  /**
   * Map raw API items to LocalMediaAsset[] with preview URLs
   */
  const _mapToLocalAssets = useCallback(
    (items: LocalMediaAsset[]): LocalMediaAsset[] => {
      const baseUrl = previewUrl
        ? stripTokenFromUrl(previewUrl).replace(/\/$/, "")
        : "";

      return items.map((file: LocalMediaAsset) => {
        const cacheBuster = new Date(file.modifiedAt).getTime();
        return {
          ...file,
          id: `local-${file.name}`,
          previewUrl: baseUrl
            ? `${baseUrl}${file.assetPath}?t=${cacheBuster}`
            : undefined,
        };
      });
    },
    [previewUrl],
  );

  // Load local assets on mount (page 1)
  const loadLocalAssets = useCallback(async () => {
    if (!devContainerId) return;

    setIsLoadingAssets(true);
    setAssetsPage(1);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/media-library/assets/${devContainerId}`,
        {
          params: { page: 1, perPage: ASSETS_PER_PAGE },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.items) {
        const assets = _mapToLocalAssets(response.data.items);
        setLocalAssets(assets);
        setTotalLocalAssets(response.data.meta?.total ?? assets.length);
        setAssetsHasMore(
          (response.data.meta?.currentPage ?? 1) <
            (response.data.meta?.totalPages ?? 1),
        );
      } else {
        setLocalAssets([]);
        setTotalLocalAssets(0);
        setAssetsHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load media assets:", error);
      setLocalAssets([]);
      setTotalLocalAssets(0);
      setAssetsHasMore(false);
    } finally {
      setIsLoadingAssets(false);
    }
  }, [devContainerId, _mapToLocalAssets]);

  // Load more local assets (next page)
  const loadMoreAssets = useCallback(async () => {
    if (!devContainerId || !assetsHasMore || isLoadingMoreAssets) return;

    const nextPage = assetsPage + 1;
    setIsLoadingMoreAssets(true);

    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/media-library/assets/${devContainerId}`,
        {
          params: { page: nextPage, perPage: ASSETS_PER_PAGE },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.items?.length > 0) {
        const newAssets = _mapToLocalAssets(response.data.items);
        setLocalAssets((prev) => [...prev, ...newAssets]);
        setAssetsPage(nextPage);
        setAssetsHasMore(
          (response.data.meta?.currentPage ?? nextPage) <
            (response.data.meta?.totalPages ?? nextPage),
        );
      } else {
        setAssetsHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load more assets:", error);
    } finally {
      setIsLoadingMoreAssets(false);
    }
  }, [devContainerId, assetsHasMore, isLoadingMoreAssets, assetsPage, _mapToLocalAssets]);

  // NOTE: loadLocalAssets is NOT called on mount. It is triggered lazily
  // when the user switches to the Downloaded tab (see media-library-panel.tsx).
  // This avoids a slow sidecar call blocking initial panel render.

  // Initial curated search on mount (like Canva)
  useEffect(() => {
    const loadInitialContent = async () => {
      // Only run on first mount when no search has been performed
      if (hasSearched) return;

      // Default curated search terms by media type
      const defaultQueries: Record<MediaType, string> = {
        image: "design",
        video: "nature",
        audio: "ambient",
      };

      const curatedQuery = defaultQueries[mediaType];

      // Set query and trigger search
      setQuery(curatedQuery);

      // Update search params ref
      currentSearchParamsRef.current = {
        mediaType,
        query: curatedQuery,
        provider,
        orientation,
        color,
        sortOrder,
        videoSize,
        videoType,
      };

      // Perform initial search
      setIsSearching(true);
      setHasSearched(true);
      setIsShowingCurated(true);
      setCurrentPage(1);

      try {
        const token = await auth.getIdToken();
        if (!token) return;

        const response = await axios.get(
          `${API_URL}/api/media-library/search`,
          {
            params: {
              query: curatedQuery,
              type: mediaType,
              provider: provider === "all" ? undefined : provider,
              perPage: 30,
              page: 1,
              orientation: orientation !== "any" ? orientation : undefined,
              color: color !== "any" ? color : undefined,
              sortOrder: sortOrder !== "popular" ? sortOrder : undefined,
              videoSize: mediaType === "video" && videoSize !== "any" ? videoSize : undefined,
              videoType: mediaType === "video" && videoType !== "all" ? videoType : undefined,
            },
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success && isMountedRef.current) {
          setSearchResults(response.data.items || []);
          setTotalResults(response.data.meta.total || 0);
          setHasMore(response.data.meta.currentPage < response.data.meta.totalPages);
        }
      } catch (error) {
        console.error("Initial content load failed:", error);
      } finally {
        if (isMountedRef.current) {
          setIsSearching(false);
          isSearchingRef.current = false;
        }
      }
    };

    loadInitialContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Search handler
  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      toast.error("Please enter search keywords");
      return;
    }

    // Prevent concurrent searches
    if (isSearchingRef.current) {
      return;
    }

    isSearchingRef.current = true;

    // Update search params ref FIRST (synchronous)
    currentSearchParamsRef.current = {
      mediaType,
      query,
      provider,
      orientation,
      color,
      sortOrder,
      videoSize,
      videoType,
    };

    // Clear previous results immediately
    setSearchResults([]);
    setTotalResults(0);
    setIsSearching(true);
    setHasSearched(true);
    setIsShowingCurated(false); // User is performing manual search
    setSelectedResults(new Set());
    setCurrentPage(1);

    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Build query params matching backend SearchMediaDto
      const params: Record<string, string | number> = {
        query: query.trim(),
        type: mediaType,
        perPage: 30,
        page: 1,
      };

      // Add optional provider filter
      if (provider !== "all") {
        params.provider = provider;
      }

      // Add optional orientation filter (images only)
      if (orientation !== "any") {
        params.orientation = orientation;
      }

      // Add optional color filter
      if (color !== "any") {
        params.color = color;
      }

      // Add optional sort order
      if (sortOrder !== "popular") {
        params.sortOrder = sortOrder;
      }

      // Add optional video-specific filters
      if (mediaType === "video" && videoSize !== "any") {
        params.videoSize = videoSize;
      }
      if (mediaType === "video" && videoType !== "all") {
        params.videoType = videoType;
      }

      const response = await axios.get(`${API_URL}/api/media-library/search`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data.success) {
        toast.error("Search failed");
        setSearchResults([]);
        setTotalResults(0);
        return;
      }

      // Deduplicate results based on provider + id
      const resultsMap = new Map<string, MediaLibraryResult>();
      (response.data.items || []).forEach((item: MediaLibraryResult) => {
        const key = `${item.provider}-${item.id}`;
        if (!resultsMap.has(key)) {
          resultsMap.set(key, item);
        }
      });
      const uniqueResults = Array.from(resultsMap.values());

      setSearchResults(uniqueResults);
      setTotalResults(response.data.meta.total || 0);
      setHasMore(response.data.meta.currentPage < response.data.meta.totalPages);
    } catch (error: unknown) {
      console.error("Search failed:", error);
      let errorMessage = "Search failed";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setIsSearching(false);
      isSearchingRef.current = false;
    }
  }, [query, mediaType, provider, orientation, color, sortOrder, videoSize, videoType]);

  // Load more results (infinite scroll)
  const loadMore = useCallback(async () => {
    // Prevent loadMore during search or when no more results
    if (!hasMore || isLoadingMore || isSearchingRef.current || !hasSearched)
      return;

    // Prevent loadMore if search params have changed (e.g., media type switch)
    const currentParams = currentSearchParamsRef.current;
    if (
      currentParams.mediaType !== mediaType ||
      currentParams.query !== query ||
      currentParams.provider !== provider ||
      currentParams.orientation !== orientation ||
      currentParams.color !== color ||
      currentParams.sortOrder !== sortOrder ||
      currentParams.videoSize !== videoSize ||
      currentParams.videoType !== videoType
    ) {
      return;
    }

    const nextPage = currentPage + 1;
    setIsLoadingMore(true);

    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const params: Record<string, string | number> = {
        query: query.trim(),
        type: mediaType,
        perPage: 30,
        page: nextPage,
      };

      if (provider !== "all") params.provider = provider;
      if (orientation !== "any") params.orientation = orientation;
      if (color !== "any") params.color = color;
      if (sortOrder !== "popular") params.sortOrder = sortOrder;
      if (mediaType === "video" && videoSize !== "any") params.videoSize = videoSize;
      if (mediaType === "video" && videoType !== "all") params.videoType = videoType;

      const response = await axios.get(`${API_URL}/api/media-library/search`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      const { items, meta } = response.data;

      if (items && items.length > 0) {
        // Deduplicate with existing results
        const existingKeys = new Set(
          searchResults.map((r) => `${r.provider}-${r.id}`),
        );
        const newResults = items.filter((item: MediaLibraryResult) => {
          return !existingKeys.has(`${item.provider}-${item.id}`);
        });

        setSearchResults((prev) => [...prev, ...newResults]);
        setCurrentPage(nextPage);
        setHasMore(meta.currentPage < meta.totalPages);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Load more failed:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    currentPage,
    query,
    mediaType,
    provider,
    orientation,
    color,
    sortOrder,
    videoSize,
    videoType,
    searchResults,
  ]);

  // Auto-search when media type changes (but don't force provider change)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      setPrevMediaType(mediaType); // Set initial value
      return;
    }

    // Only auto-search if media type actually changed
    if (prevMediaType !== mediaType && hasSearched) {
      setPrevMediaType(mediaType); // Update AFTER checking

      // Reset provider if incompatible with the new media type
      const isProviderCompatible =
        provider === "all" ||
        MEDIA_PROVIDERS.some(
          (p) => p.value === provider && p.supportedTypes.includes(mediaType),
        );
      const effectiveProvider = isProviderCompatible ? provider : "all";
      if (!isProviderCompatible) {
        setProvider("all");
      }

      // Reset pagination state, video filters, selection, and scroll FIRST
      setCurrentPage(1);
      setHasMore(false);
      setIsLoadingMore(false);
      setSearchResults([]);
      setSelectedResults(new Set());
      setVideoSize("any");
      setVideoType("all");

      // Reset scroll position to top
      if (scrollViewportRef.current) {
        scrollViewportRef.current.scrollTop = 0;
      }

      // If showing curated content, load curated for new type
      if (isShowingCurated) {
        const defaultQueries: Record<MediaType, string> = {
          image: "design",
          video: "nature",
          audio: "ambient",
        };
        const newQuery = defaultQueries[mediaType];
        setQuery(newQuery);

        // Update search params ref with corrected provider and reset video filters
        currentSearchParamsRef.current = {
          mediaType,
          query: newQuery,
          provider: effectiveProvider,
          orientation,
          color,
          sortOrder,
          videoSize: "any",
          videoType: "all",
        };

        // Perform search with new curated query and corrected provider
        (async () => {
          if (isSearchingRef.current) return;
          isSearchingRef.current = true;
          setIsSearching(true);

          try {
            const token = await auth.getIdToken();
            if (!token) return;

            const response = await axios.get(
              `${API_URL}/api/media-library/search`,
              {
                params: {
                  query: newQuery,
                  type: mediaType,
                  provider: effectiveProvider === "all" ? undefined : effectiveProvider,
                  perPage: 30,
                  page: 1,
                  orientation: orientation !== "any" ? orientation : undefined,
                  color: color !== "any" ? color : undefined,
                  sortOrder: sortOrder !== "popular" ? sortOrder : undefined,
                },
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            if (response.data.success && isMountedRef.current) {
              setSearchResults(response.data.items || []);
              setTotalResults(response.data.meta.total || 0);
              setHasMore(response.data.meta.currentPage < response.data.meta.totalPages);
            }
          } catch (error) {
            console.error("Curated content load failed:", error);
            if (axios.isAxiosError(error)) {
              toast.error(error.response?.data?.message || "Search failed");
            }
          } finally {
            if (isMountedRef.current) {
              setIsSearching(false);
              isSearchingRef.current = false;
            }
          }
        })();
      } else if (query.trim()) {
        // Search inline with corrected provider (can't use handleSearch since state hasn't updated yet)
        currentSearchParamsRef.current = {
          mediaType,
          query,
          provider: effectiveProvider,
          orientation,
          color,
          sortOrder,
          videoSize: "any",
          videoType: "all",
        };

        (async () => {
          if (isSearchingRef.current) return;
          isSearchingRef.current = true;
          setIsSearching(true);
          setIsShowingCurated(false);

          try {
            const token = await auth.getIdToken();
            if (!token) return;

            const params: Record<string, string | number> = {
              query: query.trim(),
              type: mediaType,
              perPage: 30,
              page: 1,
            };
            if (effectiveProvider !== "all") params.provider = effectiveProvider;
            if (orientation !== "any") params.orientation = orientation;
            if (color !== "any") params.color = color;
            if (sortOrder !== "popular") params.sortOrder = sortOrder;

            const response = await axios.get(
              `${API_URL}/api/media-library/search`,
              {
                params,
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            if (response.data.success && isMountedRef.current) {
              setSearchResults(response.data.items || []);
              setTotalResults(response.data.meta.total || 0);
              setHasMore(response.data.meta.currentPage < response.data.meta.totalPages);
            }
          } catch (error) {
            console.error("Search failed:", error);
            if (axios.isAxiosError(error)) {
              toast.error(error.response?.data?.message || "Search failed");
            }
          } finally {
            if (isMountedRef.current) {
              setIsSearching(false);
              isSearchingRef.current = false;
            }
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType]); // Only trigger when mediaType changes, not provider

  // Download handler
  const handleDownload = useCallback(
    async (items: MediaLibraryResult[]) => {
      if (!devContainerId) {
        toast.error("Dev container not available");
        return;
      }

      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Add items to downloading state
      const newDownloading: DownloadingMedia[] = items.map((item) => ({
        id: `dl-${item.id}-${Date.now()}`,
        provider: item.provider,
        mediaId: item.id,
        type: item.type,
        filename: item.title
          .toLowerCase()
          .replace(/[^a-z0-9_-]/g, "-")
          .replace(/-{2,}/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 60),
        thumbnailUrl: item.thumbnailUrl,
        status: "downloading" as const,
      }));

      setDownloadingItems((prev) => [...newDownloading, ...prev]);

      // Download each item
      for (const downloading of newDownloading) {
        try {
          const response = await axios.post(
            `${API_URL}/api/media-library/download`,
            {
              provider: downloading.provider,
              mediaId: downloading.mediaId,
              type: downloading.type,
              filename: downloading.filename,
              devContainerId,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (response.data.success) {
            setDownloadingItems((prev) =>
              prev.map((d) =>
                d.id === downloading.id
                  ? { ...d, status: "completed" as const }
                  : d,
              ),
            );
            toast.success(`Downloaded ${downloading.filename}`);

            // Reload assets after download
            setTimeout(() => {
              if (isMountedRef.current) {
                loadLocalAssets();
              }
            }, 500);

            // Remove from list after delay
            setTimeout(() => {
              if (isMountedRef.current) {
                setDownloadingItems((prev) =>
                  prev.filter((d) => d.id !== downloading.id),
                );
              }
            }, 3000);
          } else {
            setDownloadingItems((prev) =>
              prev.map((d) =>
                d.id === downloading.id
                  ? {
                      ...d,
                      status: "failed" as const,
                      error: response.data.error,
                    }
                  : d,
              ),
            );
            toast.error(`Failed to download: ${response.data.error}`);
          }
        } catch (error: unknown) {
          let errorMessage = "Download failed";
          if (axios.isAxiosError(error)) {
            errorMessage = error.response?.data?.message || errorMessage;
          }
          setDownloadingItems((prev) =>
            prev.map((d) =>
              d.id === downloading.id
                ? { ...d, status: "failed" as const, error: errorMessage }
                : d,
            ),
          );
          toast.error(errorMessage);
        }
      }

      // Clear selection after download
      setSelectedResults(new Set());
    },
    [devContainerId, loadLocalAssets],
  );

  // Download selected items
  const handleDownloadSelected = useCallback(async () => {
    const selectedItems = searchResults.filter((r) =>
      selectedResults.has(`${r.provider}-${r.id}`),
    );
    if (selectedItems.length === 0) {
      toast.error("No items selected");
      return;
    }
    await handleDownload(selectedItems);
  }, [searchResults, selectedResults, handleDownload]);

  // Delete selected local assets
  const handleDeleteSelected = useCallback(async () => {
    if (selectedAssets.size === 0) return;
    if (!devContainerId) {
      toast.error("Dev container not available");
      return;
    }

    const token = await auth.getIdToken();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    setIsDeleting(true);

    try {
      // Group by media type for deletion
      const toDelete = localAssets.filter((a) => selectedAssets.has(a.id));
      const byType = new Map<MediaType, string[]>();

      for (const asset of toDelete) {
        const existing = byType.get(asset.type) || [];
        existing.push(asset.name);
        byType.set(asset.type, existing);
      }

      let totalDeleted = 0;
      let totalFailed = 0;

      for (const [type, filenames] of byType) {
        const response = await axios.post(
          `${API_URL}/api/media-library/assets/${devContainerId}/delete`,
          { filenames, type },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        totalDeleted += response.data.deleted || 0;
        totalFailed += response.data.failed || 0;
      }

      if (totalDeleted > 0) {
        toast.success(
          `Deleted ${totalDeleted} item${totalDeleted > 1 ? "s" : ""}`,
        );
        setLocalAssets((prev) => prev.filter((a) => !selectedAssets.has(a.id)));
        setSelectedAssets(new Set());
      }

      if (totalFailed > 0) {
        toast.error(`Failed to delete ${totalFailed} item(s)`);
      }
    } catch (error: unknown) {
      console.error("Delete failed:", error);
      let errorMessage = "Delete failed";
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedAssets, devContainerId, localAssets]);

  // Selection handlers for search results
  const toggleResultSelection = useCallback((id: string) => {
    setSelectedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const clearResultSelection = useCallback(() => {
    setSelectedResults(new Set());
  }, []);

  const selectAllResults = useCallback(() => {
    setSelectedResults(new Set(searchResults.map((r) => `${r.provider}-${r.id}`)));
  }, [searchResults]);

  // Selection handlers for local assets
  const toggleAssetSelection = useCallback((id: string) => {
    setSelectedAssets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const clearAssetSelection = useCallback(() => {
    setSelectedAssets(new Set());
  }, []);

  const selectAllAssets = useCallback(() => {
    setSelectedAssets(new Set(localAssets.map((a) => a.id)));
  }, [localAssets]);

  // Derive set of currently-downloading media IDs for UI loading indicators
  const downloadingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of downloadingItems) {
      if (item.status === "downloading") {
        ids.add(`${item.provider}-${item.mediaId}`);
      }
    }
    return ids;
  }, [downloadingItems]);

  return {
    // Search state
    query,
    setQuery,
    mediaType,
    setMediaType,
    provider,
    setProvider,
    orientation,
    setOrientation,
    color,
    setColor,
    sortOrder,
    setSortOrder,
    videoSize,
    setVideoSize,
    videoType,
    setVideoType,

    // Search results
    searchResults,
    totalResults,
    isSearching,
    hasSearched,
    isShowingCurated,
    handleSearch,

    // Infinite scroll
    hasMore,
    isLoadingMore,
    loadMore,

    // Selection
    selectedResults,
    toggleResultSelection,
    clearResultSelection,
    selectAllResults,

    // Download
    downloadingItems,
    downloadingIds,
    handleDownload,
    handleDownloadSelected,

    // Local assets
    localAssets,
    totalLocalAssets,
    isLoadingAssets,
    loadLocalAssets,

    // Local assets pagination
    assetsHasMore,
    isLoadingMoreAssets,
    loadMoreAssets,

    // Local asset selection
    selectedAssets,
    toggleAssetSelection,
    clearAssetSelection,
    selectAllAssets,

    // Deletion
    isDeleting,
    handleDeleteSelected,

    // Scroll control
    scrollViewportRef,
  };
}
